import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { Zone, ZoneMetadata } from "@common/ADAPT.Common.Model/methodology/zone";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamLocation } from "@common/ADAPT.Common.Model/organisation/team-location";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { IDxListItemDeletedEvent } from "@common/ux/dx.types";
import { OrganisationMapService } from "@org-common/lib/architecture/organisation-map/organisation-map.service";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { TeamHierarchyService } from "@org-common/lib/teams/team-hierarchy.service";
import ArrayStore from "devextreme/data/array_store";
import DataSource, { DataSourceOptions } from "devextreme/data/data_source";
import { ValueChangedEvent } from "devextreme/ui/select_box";
import { lastValueFrom } from "rxjs";

@Component({
    selector: "adapt-edit-team-locations",
    templateUrl: "./edit-team-locations.component.html",
    standalone: false,
})
export class EditTeamLocationsComponent extends BaseComponent implements OnInit {
    public readonly getAsZoneLocation = TeamLocation.getAsZoneLocation;
    public readonly getAsParentTeamLocation = TeamLocation.getAsParentTeamLocation;
    public readonly getAsChildTeamLocation = TeamLocation.getAsChildTeamLocation;

    @Input() public team!: Team;
    @Input() public disabled = false;
    @Input() public showLabel = true;
    @Input() public showChildSelection = true;

    @Output() public entitiesPopulated = new EventEmitter<TeamLocation[]>();
    @Output() public entityChanged = new EventEmitter<TeamLocation>();

    public parentTeamLocations: TeamLocation[] = [];
    public childTeamLocations: TeamLocation[] = [];
    public activeChildTeamLocations: TeamLocation[] = [];
    public inactiveChildTeamLocations: TeamLocation[] = [];

    public allParentLocations: (Partial<TeamLocation> & { name: string; })[] = [];
    public allChildLocations: (Partial<TeamLocation> & { name: string; })[] = [];

    public parentDataSource?: DataSource<TeamLocation, number>;
    public childDataSource?: DataSource<TeamLocation, number>;

    public constructor(
        private commonDataService: CommonDataService,
        private orgService: OrganisationService,
        private orgMapService: OrganisationMapService,
        private commonTeamsService: CommonTeamsService,
        private teamHierarchyService: TeamHierarchyService,
    ) {
        super();
    }

    public async ngOnInit() {
        await this.refreshData();
        this.entitiesPopulated.emit([...this.parentTeamLocations, ...this.childTeamLocations]);
    }

    public async refreshData() {
        this.allParentLocations = [];
        this.allChildLocations = [];

        for (const zone of ZoneMetadata.InOrder) {
            let name = ZoneMetadata.Name[zone];

            if (zone === Zone.ResearchAndDevelopment) {
                const config = await lastValueFrom(this.orgService.getOrganisationConfiguration());
                if (config && config.productZoneName) {
                    name = config.productZoneName;
                }
            }

            this.allParentLocations.push({ zone, name });
        }

        const teams = await this.commonTeamsService.promiseToGetAllActiveTeams();
        for (const team of teams.sort((a, b) => a.name.localeCompare(b.name))) {
            this.allParentLocations.push({ parentTeam: team, parentTeamId: team.teamId, name: team.name });
            this.allChildLocations.push({
                team,
                teamId: team.teamId,
                parentTeam: this.team,
                parentTeamId: this.team.teamId,
                name: team.name,
            });
        }

        this.parentTeamLocations = this.team
            ? await lastValueFrom(this.commonTeamsService.getTeamLocations(this.team))
            : [];

        this.childTeamLocations = this.team
            ? await lastValueFrom(this.orgMapService.getLocationItems({ parentTeamId: this.team.teamId }, true))
            : [];

        [this.activeChildTeamLocations, this.inactiveChildTeamLocations] = ArrayUtilities.partition(
            this.childTeamLocations, (location) => location.team.isActive());
        this.parentTeamLocations.sort(TeamLocation.compare);

        const { ancestorTeamIds, descendantTeamIds } = await this.teamHierarchyService
            .promiseToGetAncestorAndDescendantTeamIds(this.team);

        this.parentDataSource = new DataSource({
            store: new ArrayStore({
                data: this.allParentLocations,
            }),
            group: TeamLocation.group,
            filter: this.filterOutExistingParentLocations.bind(this, descendantTeamIds),
        } as DataSourceOptions<TeamLocation>);

        this.childDataSource = new DataSource({
            store: new ArrayStore({
                data: this.allChildLocations,
            }),
            group: (location) => `${location.team?.teamTypeName}s`,
            filter: this.filterOutExistingChildLocations.bind(this, ancestorTeamIds),
        } as DataSourceOptions<TeamLocation>);
    }

    @Autobind
    public filterOutExistingParentLocations(descendantTeamIds: Set<number>, location: Pick<TeamLocation, "parentTeam" | "parentTeamId" | "zone">) {
        return location.parentTeam !== this.team
            && !this.parentTeamLocations.find((i) => i.parentTeamId === location.parentTeamId || i.zone === location.zone)
            && !this.childTeamLocations.find((i) => i.teamId === location.parentTeamId)
            && !descendantTeamIds.has(location.parentTeamId!);
    }

    @Autobind
    public filterOutExistingChildLocations(ancestorTeamIds: Set<number>, location: Pick<TeamLocation, "team" | "teamId">) {
        return location.team !== this.team
            && !this.childTeamLocations.find((i) => i.teamId === location.teamId)
            && !ancestorTeamIds.has(location.teamId!);
    }

    public async onSelectValueChange(event: ValueChangedEvent, isParent = true) {
        if (event.value && event.previousValue !== event.value) {
            event.component.reset();

            let teamLocation: TeamLocation | undefined;

            if (isParent) {
                // zone can only be a parent
                const zone = TeamLocation.getAsZoneLocation(event.value);
                if (zone) {
                    teamLocation = await lastValueFrom(this.orgMapService.addTeamToZone(this.team, zone));
                }

                const team = TeamLocation.getAsParentTeamLocation(event.value);
                if (team) {
                    teamLocation = await lastValueFrom(this.commonTeamsService.addChildTeamForParentTeam(this.team, team));
                }
            } else {
                const team = TeamLocation.getAsChildTeamLocation(event.value);
                if (team) {
                    teamLocation = await lastValueFrom(this.commonTeamsService.addChildTeamForParentTeam(team, this.team));
                }
            }

            if (teamLocation) {
                this.entityChanged.emit(teamLocation);
            }

            await this.refreshData();
        }
    }

    public async removeTeamLocation(e: IDxListItemDeletedEvent<TeamLocation>) {
        await lastValueFrom(this.commonDataService.remove(e.itemData));
        ArrayUtilities.removeElementFromArray(e.itemData, this.parentTeamLocations);
        this.entityChanged.emit(e.itemData);
        await this.refreshData();
    }

    public async reorderItems() {
        if (!this.childTeamLocations) {
            return;
        }

        // DevExtreme has already moved the item in the array to the correct position.
        const reorderableLocations = this.activeChildTeamLocations;
        SortUtilities.sequenceNumberFieldInArray(reorderableLocations, "ordinal");

        // We could be smart and only add the key functions that actually changed, but they will be filtered out by
        // breeze on save anyway
        for (const teamLocation of reorderableLocations) {
            this.entityChanged.emit(teamLocation);
        }

        await this.refreshData();
    }

    public async onItemDeleted({ itemData }: IDxListItemDeletedEvent<TeamLocation>) {
        await lastValueFrom(this.commonDataService.remove(itemData));
        this.entityChanged.emit(itemData);
        await this.refreshData();
    }
}
